import React from "react";
import { Box, Button, CardContent, Grid, IconButton, makeStyles, TextField, Typography } from "@material-ui/core";
import moment from "moment";
import HelpIcon from "@material-ui/icons/Help";
import SkipPreviousIcon from "@material-ui/icons/SkipPrevious";
import SkipNextIcon from "@material-ui/icons/SkipNext";
import TodayIcon from "@material-ui/icons/Today";
import { FormattedMessage, injectIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Autocomplete } from "@material-ui/lab";

import HeaderPageItem from "../custom/HeaderPageItem";
import PageContainer, { CardPageItem } from "../custom/Page";
import DatePickerByPlatform from "./components/DatePickerByPlatform";
import LoadingSpinner from "./components/LoadingSpinner";
import PaymentsTable from "./components/Reports/ReportManagersAndAgents/PaymentsTable";
import PaymentsTotal from "./components/Reports/ReportManagersAndAgents/PaymentsTotal";
import HelpReportAgentsFinancial from "./components/Reports/HelpDialog/HelpReportAgentsFinancial";

import { PeriodType, Roles } from "../util";
import useFetchAgentsBelow from "../hooks/useFetchAgentsBelow";

const useStyles = makeStyles((theme) => ({
  marginBottom2: {
    marginBottom: theme.spacing(2),
  },
  dateNavigation: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  dateNavGroup: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  buttonActualMonth: {
    padding: theme.spacing(1),
  },
  textAlignCenter: {
    textAlign: "center",
    marginTop: theme.spacing(2),
  },
  centerBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
  },
}));

const MonthOption = {
  CURRENT: "CURRENT",
  PREVIOUS: "PREVIOUS",
  NEXT: "NEXT",
};

const ReportAgentsFinancial = ({ history, intl }) => {
  const classes = useStyles();

  const currentUser = useSelector((state) => state.user.user.me);
  const [startDate, setStartDate] = React.useState(moment().startOf("month"));
  const [finalDate, setFinalDate] = React.useState(moment().endOf("month"));
  const [error, setError] = React.useState(false);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [agent, setAgent] = React.useState(currentUser.role === Roles.Agent ? currentUser : null);

  const { agents, isLoading, error: errorAgents } = useFetchAgentsBelow(currentUser, true);

  function onStartDateChange(date) {
    if (!date?.isValid()) return;

    if (finalDate && date.isAfter(finalDate)) {
      setError("app.date.startAfterEnd");
      return;
    }

    setError(null);
    setStartDate(date);
  }

  function onFinalDateChange(date) {
    if (!date?.isValid()) return;

    if (startDate && date.isBefore(startDate)) {
      setError("app.date.finalBeforeStart");
      return;
    }

    setError(null);
    setFinalDate(date);
  }

  function onMonthClick(option) {
    switch (option) {
      case MonthOption.CURRENT:
        setStartDate(moment().startOf(PeriodType.MONTH));
        setFinalDate(moment().endOf(PeriodType.MONTH));
        break;
      case MonthOption.PREVIOUS:
        setStartDate(moment(startDate).subtract(1, PeriodType.MONTH).startOf(PeriodType.MONTH));
        setFinalDate(moment(finalDate).subtract(1, PeriodType.MONTH).endOf(PeriodType.MONTH));
        break;
      case MonthOption.NEXT:
        setStartDate(moment(startDate).add(1, PeriodType.MONTH).startOf(PeriodType.MONTH));
        setFinalDate(moment(finalDate).add(1, PeriodType.MONTH).endOf(PeriodType.MONTH));
        break;
      default:
        break;
    }
  }

  const dateLabel = React.useMemo(() => {
    if (!startDate || !finalDate) return "";

    const isFullMonth = startDate.isSame(moment(startDate).startOf("month")) && finalDate.isSame(moment(startDate).endOf("month"));

    if (isFullMonth) {
      return startDate.format("MMMM/YYYY").replace(/^./, c => c.toUpperCase());
    }

    return `${startDate.format("L")} - ${finalDate.format("L")}`;
  }, [startDate, finalDate]);

  const pageError = React.useMemo(() => error || errorAgents, [error, errorAgents]);

  return (
    <PageContainer full>
      <HeaderPageItem
        title={<FormattedMessage id="app.report.manager.tittle" />}
        preserveTitle
        onBack={() => history.goBack()}
        onMore={() => setOpenDialog(true)}
        IconMore={HelpIcon}
      />

      <PaymentsTotal
        agent={agent}
        currentUser={currentUser}
        dateLabel={dateLabel}
        startDate={startDate}
        finalDate={finalDate}
      />

      <CardPageItem
        raised
        double
      >
        <CardContent className={classes.centerBox}>
          <Grid
            container
            spacing={2}
          >
            <Grid
              item
              xs={12}
              md={12}
              className={classes.marginBottom2}
            >
              <Autocomplete
                options={agents}
                getOptionLabel={(option) => `${option.name} (${option.id})`}
                value={agent}
                onChange={(event, newValue) => setAgent(newValue)}
                disabled={isLoading || pageError}
                autoHighlight
                noOptionsText={<FormattedMessage id="app.agents.notFound" />}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={<FormattedMessage id={agent ? "app.agent" : "app.report.agent.select"} />}
                    variant="outlined"
                    fullWidth
                    placeholder={intl.formatMessage({ id: "app.report.agent.filterHint" })}
                  />
                )}
                renderOption={(option) => (
                  <Box
                    display="flex"
                    width="100%"
                    justifyContent="space-between"
                  >
                    <Box
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {option.name}
                    </Box>
                    <Box ml={2}>({option.id})</Box>
                  </Box>
                )}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <DatePickerByPlatform
                value={startDate}
                onChange={onStartDateChange}
                label={<FormattedMessage id="app.date.start" />}
                disabled={isLoading}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <DatePickerByPlatform
                value={finalDate}
                onChange={onFinalDateChange}
                label={<FormattedMessage id="app.date.final" />}
                disabled={isLoading}
              />
            </Grid>
            <Grid
              item
              xs={12}
              className={classes.dateNavigation}
            >
              <Box className={classes.dateNavGroup}>
                <IconButton
                  onClick={() => onMonthClick(MonthOption.PREVIOUS)}
                  disabled={isLoading}
                >
                  <SkipPreviousIcon />
                </IconButton>
                <Typography>{dateLabel}</Typography>
                <IconButton
                  onClick={() => onMonthClick(MonthOption.NEXT)}
                  disabled={isLoading}
                >
                  <SkipNextIcon />
                </IconButton>
              </Box>
              <Button
                variant="text"
                size="small"
                onClick={() => onMonthClick(MonthOption.CURRENT)}
                className={classes.buttonActualMonth}
                endIcon={<TodayIcon />}
                disabled={isLoading}
              >
                <FormattedMessage id="app.month.current.button" />
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </CardPageItem>

      <CardPageItem>
        {pageError ? (
          <Typography
            color="error"
            variant="h6"
            className={classes.textAlignCenter}
          >
            <FormattedMessage
              id={pageError}
              defaultMessage={pageError}
            />
          </Typography>
        ) : isLoading ? (
          <LoadingSpinner
            marginTop={20}
            message={<FormattedMessage id="app.agents.searching" />}
          />
        ) : !agent ? (
          <Typography
            color="primary"
            variant="h6"
            className={classes.textAlignCenter}
          >
            <FormattedMessage id="app.report.agent.select" />
          </Typography>
        ) : (
          <PaymentsTable
            agent={agent}
            startDate={startDate}
            finalDate={finalDate}
            currentUser={currentUser}
            dateLabel={dateLabel}
          />
        )}
      </CardPageItem>

      <HelpReportAgentsFinancial
        open={openDialog}
        setOpen={setOpenDialog}
        enableBonusManager
        enableBonusManagerOneTime
      />
    </PageContainer>
  );
};

export default injectIntl(ReportAgentsFinancial);
